import React, { useState, useEffect } from 'react';
import { fetchSpecialties } from '../../../../services/api';

const EditorPanel = ({ isOpen, mode, template, onClose, onSave, providerInfo, authData }) => {
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '', 
    content: '', 
    template_type: 'full', 
    specialty: [],
    is_favorite: false,
    is_default: false
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [availableSpecialties, setAvailableSpecialties] = useState([]);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);

  // Fetch specialties when panel opens
  useEffect(() => {
    const loadSpecialties = async () => {
      if (isOpen && authData?.api_token) {
        setLoadingSpecialties(true);
        try {
          const specialties = await fetchSpecialties(authData.api_token);
          setAvailableSpecialties(specialties || []);
        } catch (error) {
          // Fallback to default specialties if fetch fails
          setAvailableSpecialties([]);
        } finally {
          setLoadingSpecialties(false);
        }
      }
    };
    
    loadSpecialties();
  }, [isOpen, authData]);

  useEffect(() => {
    if (isOpen) {
      console.log('[EditorPanel] authData:', authData);
      console.log('[EditorPanel] provider_data:', authData?.provider_data);
      console.log('[EditorPanel] specialties:', authData?.provider_data?.specialties);
      
      // Use first available specialty as default if no provider specialties
      const allSpecialtyNames = availableSpecialties.length > 0 
        ? [availableSpecialties[0].name]
        : ['General'];
      
      if (template) {
        // Handle both edit mode and create mode with pre-populated data
        const templateSpecialties = Array.isArray(template.specialty) ? template.specialty : 
                                  template.specialty ? [template.specialty] : allSpecialtyNames;
        setFormData({ 
          name: template.name || '', 
          description: template.description || '', 
          content: template.content || '', 
          template_type: template.template_type || 'full', 
          specialty: templateSpecialties,
          is_favorite: template.is_favorite || false,
          is_default: template.is_default || false
        });
      } else {
        // Empty form for new templates
        setFormData({ 
          name: '', 
          description: '', 
          content: '', 
          template_type: 'full', 
          specialty: allSpecialtyNames,
          is_favorite: false,
          is_default: false
        });
      }
      setError(null);
      setSuccessMessage(null);
    }
  }, [isOpen, mode, template, authData, availableSpecialties]);

  const handleSave = async () => {
    if (!formData.name.trim()) { setError('Template name is required'); return; }
    if (!formData.content.trim()) { setError('Template content is required'); return; }

    setSaving(true);
    setError(null);
    
    try {
      // Convert selected specialty names to UUIDs for backend
      // Use the fetched availableSpecialties list instead of provider's specialties
      const selectedSpecialtyUUIDs = formData.specialty.map(selectedName => {
        const specialtyObj = availableSpecialties.find(s => s.name === selectedName);
        return specialtyObj?.id;
      }).filter(Boolean);
      
      const templateData = {
        ...formData,
        specialties: selectedSpecialtyUUIDs
      };
      delete templateData.specialty; // Remove the frontend field
      await onSave(templateData);
      setSuccessMessage(mode === 'create' ? 'Template created successfully. Try it out with the "Apply" button!' : 'Template updated successfully. Try it out with the "Apply" button!');
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`template-editor-panel ${isOpen ? 'open' : ''}`}>
      <div className="scribe-panel-header">
        <h3 className="popup-title">{mode === 'create' ? 'Create Template' : 'Edit Template'}</h3>
        <button className="close-button" onClick={onClose}>×</button>
      </div>

      <div className="scribe-panel-content" style={{ padding: '16px', overflow: 'auto' }}>
        {error && <div className="ankr-auth-status error">{error}</div>}
        {successMessage && <div className="ankr-auth-status success">{successMessage}</div>}

        <div className="ankr-form-group">
          <label>Template Name *</label>
          <input type="text" className="search-input" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} placeholder="Enter template name..." />
        </div>

        <div className="ankr-form-group">
          <label>Description</label>
          <textarea className="search-input" value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} placeholder="Brief description of the template..." rows="2" style={{ resize: 'vertical', minHeight: '60px' }} />
        </div>

        <div className="ankr-form-group">
          <label>Type</label>
          <select className="select" value={formData.template_type} onChange={(e) => setFormData(prev => ({ ...prev, template_type: e.target.value }))}>
            <option value="full">Full Note Template</option>
            <option value="section">Section Template</option>
          </select>
        </div>

        <div className="ankr-form-group">
          <label>Specialties</label>
          <select 
            className="select" 
            multiple 
            value={formData.specialty} 
            onChange={(e) => {
              const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
              setFormData(prev => ({ ...prev, specialty: selectedOptions }));
            }}
            style={{ minHeight: '80px' }}
            disabled={loadingSpecialties}
          >
            {loadingSpecialties ? (
              <option disabled>Loading specialties...</option>
            ) : availableSpecialties.length > 0 ? (
              availableSpecialties.map(specialty => (
                <option key={specialty.id} value={specialty.name}>{specialty.name}</option>
              ))
            ) : (
              // Fallback if no specialties available
              <option disabled>No specialties available</option>
            )}
          </select>
          <small style={{ color: '#666', fontSize: '12px' }}>Hold Ctrl/Cmd to select multiple</small>
        </div>

        {/* Template Preferences - show for both create and edit modes */}
        <div className="ankr-form-group">
          <label>Template Preferences</label>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginTop: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.is_favorite}
                onChange={(e) => setFormData(prev => ({ ...prev, is_favorite: e.target.checked }))}
              />
              <span>❤️ Mark as Favorite</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.is_default}
                onChange={(e) => setFormData(prev => ({ ...prev, is_default: e.target.checked }))}
              />
              <span>⭐ Set as Default</span>
            </label>
          </div>
          <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
            Default template will be auto-selected for new visit notes
          </small>
        </div>

        <div className="ankr-form-group">
          <label>Template Content *</label>
          <textarea className="search-input" value={formData.content} onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))} placeholder="Enter your template content here..." rows="10" style={{ resize: 'vertical', minHeight: '200px', fontFamily: 'monospace' }} />
        </div>
      </div>

      <div className="scribe-status-bar">
        <div className="status-left">
          <button className="button primary mr-2" onClick={handleSave} disabled={saving || !formData.name.trim() || !formData.content.trim()}>{saving ? 'Saving...' : (mode === 'create' ? 'Create Template' : 'Save Changes')}</button>
          <button className="button" onClick={onClose} disabled={saving}>Cancel</button>
        </div>
        <div className="status-right"></div>
      </div>
    </div>
  );
};

export default EditorPanel;